import Image from 'next/image';
import formatCurrency from '@/utilities/formatCurrency';
import { useEffect, useState } from 'react';
import { getMaxAllowedQuantity } from '@/utilities/getMaxAllowedQuantity';
import useCartRemove from '@/hooks/useCartRemove';
import useCartUpdate from '@/hooks/useCartUpdate';
import { otherVariantsInCart, parseVariantName } from '@/utilities/variants';
import { ProductVariant } from '@/types/storefront';

interface ItemCardProps {
  itemName: string;
  itemCost: string;
  itemImage: string;
  lineId: string;
  quantity: number;
  maxQuantity: number;
  variantId: string;
  availableQuantity: ProductVariant["quantityAvailable"];
  variantName: string;
  productId: string;
  updatingCart: boolean;
  setUpdatingCart: (bool: boolean) => void;
}

const ItemCard = ({
  itemName,
  itemCost,
  itemImage,
  lineId,
  quantity,
  maxQuantity,
  variantId,
  availableQuantity,
  variantName,
  productId,
  updatingCart,
  setUpdatingCart
}: ItemCardProps) => {
  const max = getMaxAllowedQuantity(variantId, maxQuantity)
  const [quantityState, setQuantityState] = useState<number>(quantity);
  const removeFromCart = useCartRemove(lineId);
  const updateCart = useCartUpdate(lineId);

  const handleCartUpdate = async (quantity: number) => {
    try {
      setUpdatingCart(true)
      await updateCart(quantity);
      setUpdatingCart(false)
    } catch (error) {
      console.error(error);
      setQuantityState(quantity); // rollback
    } finally {
      setUpdatingCart(false)
    }
  }

  useEffect(() => {
    if (quantityState !== quantity) handleCartUpdate(quantityState);
  }, [quantityState, quantity]);

  return (
    <div className="flex my-8">
      <Image
        src={itemImage}
        alt="product"
        width={100}
        height={125}
        className="object-cover"
      />

      <div className="flex flex-col ml-4 flex-1 gap-1">
        <p className="font-header font-light italic text-2xl">{itemName}</p>
        <p className='font-light italic text-sm'>{parseVariantName(variantName)}</p>
        <p className="mt-2">{formatCurrency(Number(itemCost))}</p>
        <div className="flex-grow" />
        <div className="flex mt-3 mb-1">
          <div className="flex gap-4">
            <button
              className="disabled:opacity-50"
              disabled={quantityState === 1 || updatingCart}
              onClick={() => setQuantityState((state) => state - 1)}
            >
              <Image src="/icons/minus.svg" alt="minus" width={10} height={8} />
            </button>
            {quantityState}
            <button
              className="disabled:opacity-50"
              disabled={quantityState >= (max - otherVariantsInCart(variantId, productId)) || quantityState >= availableQuantity! || updatingCart}
              onClick={() => setQuantityState((state) => state + 1)}
            >
              <Image src="/icons/plus.svg" alt="plus" width={10} height={10} />
            </button>
          </div>
          <button
            className="underline font-light text-sm uppercase ml-auto cursor-pointer"
            onClick={() => removeFromCart()}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemCard;
